import {
    Button,
    classNames,
    FDate,
    Flexer,
    Header,
    Heading,
    Icon,
    logger,
    plural,
    Portal,
    ScrollView,
    stopEvent,
    Text,
    useCustomEvent,
    useDialog,
    useToast,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbChevronRight, TbConfetti, TbFilters, TbGif, TbMessage2Bolt, TbPaperclip } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { Block, Error, Loading } from '../components'
import { PresenceAvatar } from '../components/common/presence-avatar'
import { Messages } from '../components/messages'
import { byCreateAt, byUpdatedAt, isRichTextBlank, useDocumentTitle } from '../helpers'
import { useNotifications, useThreads } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'
import { SidebarArea } from './sidebar.layout'
import { MinimizeHeader } from '../components/minimize-header'
import { useWebsocket } from '../hooks/websocket.hook'

export const Thread = (props) => {
    const {
        createdAt,
        updatedAt,
        id,
        unread,
        projectMessageContent,
        projectMessageId,
        projectMessageCreatedAt,
        projectMessageUpdatedAt,
        projectMessageGifId,
        projectName,
        projectColor,
        teamId,
        teamName,
        replies,
        userId,
        userName,
        userImage,
        onDelete,
        onOpen,
    } = props
    const date = useMemo(() => FDate(new Date(projectMessageUpdatedAt)).fromNow(), [projectMessageUpdatedAt])
    const isBlank = useMemo(() => isRichTextBlank(projectMessageContent), [projectMessageContent])
    const thread = useAppStore((state) => state.thread)
    const className = classNames({
        'thread': true,
        'read': !unread,
        'reveal-on-hover': true,
    })

    return (
        <View
            column
            className={className}
            alignItems="flex-start"
            gap="1rem"
            width="100%"
            p="1rem 1.5rem"
            position="relative"
            onClick={onOpen}>
            {unread && (
                <View
                    position="absolute"
                    width={5}
                    height="calc(100% + 1px)"
                    style={{ left: -1, top: 0 }}
                    bgToken="accent"
                />
            )}

            <View
                row
                width="100%"
                gap="1rem">
                <PresenceAvatar
                    userId={userId}
                    src={userImage}
                    name={userName}
                />
                <View
                    column
                    alignItems="flex-start"
                    gap="0.25rem"
                    flex={1}>
                    {!isBlank && (
                        <View
                            row
                            width="100%"
                            justifyContent="flex-start"
                            gap="1rem">
                            <div
                                dangerouslySetInnerHTML={{ __html: projectMessageContent }}
                                className="f-text clamp-text threads-text"
                            />
                        </View>
                    )}

                    <View
                        row
                        gap="1rem"
                        alignItems="flex-start"
                        width="100%"
                        colorToken="text-weak"
                        flex={1}>
                        <View
                            row
                            gap="0.5rem"
                            flex={1}
                            width="100%">
                            <Text
                                color={projectColor}
                                size="sm"
                                fontWeight={600}>
                                {projectName}
                            </Text>
                            <Text>{userName}</Text>
                            <Text colorToken="text-weaker">{date}</Text>
                            <Text
                                size="sm"
                                fontWeight={600}>
                                {projectName}
                            </Text>
                            {teamName && (
                                <>
                                    <Icon
                                        icon={TbFilters}
                                        size="sm"
                                    />
                                    <Text
                                        size="sm"
                                        fontWeight={600}>
                                        {teamName}
                                    </Text>
                                </>
                            )}
                            {/* gif */}
                            {isBlank && !!projectMessageGifId && (
                                <Icon
                                    icon={TbGif}
                                    size="lg"
                                />
                            )}
                            {/* attachment */}
                            {isBlank && !projectMessageGifId && <Icon icon={TbPaperclip} />}
                            <Flexer />
                            <Button
                                size="sm"
                                className="reveal-on-hover-target"
                                height="1.75rem"
                                m="0 0 0 auto"
                                onClick={(e) => {
                                    stopEvent(e)
                                    onDelete()
                                }}>
                                Unsubscribe
                            </Button>
                        </View>
                    </View>
                </View>
            </View>
        </View>
    )
}

export const ThreadsLayout = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const sidebar = useAppStore((state) => state.sidebar)
    const [page, setPage] = useState(1)
    const pageSize = 8
    const { workspaceId } = useParams()
    const { showToast } = useToast()
    const notificationsAPI = useNotifications()
    const threadsAPI = useThreads()
    const { setDialog, closeDialog } = useDialog()
    const deleteDialog = useVisibility()
    const navigate = useNavigate()
    const [thread, setThread] = useState(null)
    const actions = useAppStore((state) => state.actions)
    const threads = useAppStore((state) => state.threads)
    const threadStore = useAppStore((state) => state.thread)
    const notifications = useAppStore((state) => state.notifications)
    const busyFetching = useRef(false)
    const sortedThreads = useMemo(() => threads.sort(byUpdatedAt).reverse(), [threads])
    const { join } = useWebsocket()

    const handleOpen = async (thread) => {
        if (threadStore.threadId == thread.id) return

        // join the room
        join(thread.projectId)

        // just for teamId & projectId
        setThread(thread)

        // set the project id so we have a sending target room
        actions.setProject({ id: thread.projectId })

        // set our thread
        actions.setThread({
            id: thread.projectMessageId,
            threadId: thread.id,
            messages: [],
        })

        // set the thread as read only if there are unreads
        if (thread.unread) {
            threadsAPI.update(workspaceId, thread.id, { unread: false })
            actions.threadsUpdate({ id: thread.id, unread: false })
        }

        setTimeout(() => {
            actions.setSidebar('thread')
        })
    }

    const handleDelete = async (thread) => {
        const threadId = thread.id

        setDialog({
            title: 'Are you sure?',
            description: 'Are you sure you want to unsubscribe from this thread?',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            threadsAPI
                                .remove(workspaceId, threadId)
                                .then(() => {
                                    showToast({ message: 'The thread has been removed.', anchor: 'bottom-right' })
                                    actions.threadsDelete(threadId)
                                    setLoading(false)
                                    closeDialog()
                                })
                                .catch((e) => {
                                    setLoading(false)
                                    setError('An error occurred.')
                                    closeDialog()
                                })
                        }}
                        variant="danger">
                        Unsubscribe
                    </Button>
                </View>
            ),
        })
    }

    const handleBottomScroll = () => {
        if (busyFetching.current) return
        setPage(page + 1)
    }

    const fetchData = async () => {
        // already initially loaded
        // setLoading(true)
        setError(null)

        try {
            busyFetching.current = true
            const newThreads = await threadsAPI.getWorkspace(workspaceId)

            // actions.setThreads([...threads, ...newThreads])
            // actions.setSections([])
            actions.setThreads(newThreads)

            setLoading(false)
            busyFetching.current = false
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
            busyFetching.current = false
        }
    }

    useDocumentTitle('Threads')

    useCustomEvent('reload', () => {
        if (workspaceId) fetchData()
    })

    useEffect(() => {
        fetchData()
    }, [workspaceId])

    return (
        <>
            <View
                column
                flex={1}
                width="100%"
                height="calc(100% - (var(--frame-padding) * 2))"
                position="relative"
                justifyContent="stretch"
                style={{
                    overflow: 'hidden',
                    borderTopLeftRadius: 'var(--frame-radius)',
                    borderTopRightRadius: 'var(--frame-radius)',
                    borderBottomLeftRadius: 'var(--frame-radius)',
                    borderBottomRightRadius: 'var(--frame-radius)',
                    border: 'var(--frame-border)',
                }}>
                <Error error={error} />
                <Loading loading={loading} />
                <MinimizeHeader>
                    <Heading as="h5">Threads</Heading>
                    <Flexer />
                </MinimizeHeader>

                {/*     <Header
                    row
                    gap="0rem"
                    p="0rem 2rem"
                    height="5rem"
                    m="-1rem 0 0 0"
                    justifyContent="flex-start">
                    <Block
                        name="Inbox"
                        icon="main:threads"
                        size="xl"
                        inProjectLayout
                        color="var(--f-color-accent)"
                    />

                    <Heading
                        as="h3"
                        className="main-heading">
                        Threads
                    </Heading>

                    <Flexer />
                </Header> */}

                {/* 
                <Header p="0 1rem 1rem 1rem">
                    <Text
                        flex={1}
                        size="sm"
                        colorToken="text-weaker">
                        {threads.length} {plural(threads.length, 'Thread')}
                    </Text>
                </Header>
                */}

                <View
                    column
                    flex={1}
                    width="100%"
                    height="100%"
                    position="relative"
                    justifyContent="stretch">
                    <ScrollView
                        width="100%"
                        height="100%"
                        style={{ top: 0, left: 0 }}
                        bgToken="surface"
                        position="absolute"
                        p="0"
                        className="f-overflow-y-auto"
                        onScrollToBottom={handleBottomScroll}>
                        {!threads.length && !loading && (
                            <View
                                column
                                position="absolute"
                                style={{ inset: 0 }}
                                gap="1rem">
                                <Icon
                                    icon={TbMessage2Bolt}
                                    color="var(--f-color-surface-strongest)"
                                    style={{
                                        '--f-icon-sizing-md': '7rem',
                                        '--f-icon-stroke-width-md': '1',
                                    }}
                                />
                                <Heading
                                    as="h4"
                                    fontWeight={600}>
                                    No threads!
                                </Heading>
                                <Text>You don't have any open threads right now.</Text>
                            </View>
                        )}

                        {sortedThreads.map((thread, index) => (
                            <Thread
                                key={index}
                                onDelete={() => handleDelete(thread)}
                                onOpen={() => handleOpen(thread)}
                                {...thread}
                            />
                        ))}
                    </ScrollView>
                </View>
            </View>

            {sidebar != 'thread' && <div style={{ width: 'var(--frame-padding)' }} />}

            {sidebar == 'thread' && !!thread && (
                <SidebarArea key="FRONTEER_THREADS_WIDTH">
                    <Messages
                        isSidebar
                        isThreadView
                        hideBackArrow
                        workspaceId={workspaceId}
                        projectId={thread.projectId}
                        teamId={thread.teamId ?? null}
                    />
                </SidebarArea>
            )}
        </>
    )
}
